/**
 * ProductPurchasePage — Per-product purchase page.
 *
 * Reads :slug from the URL and renders the appropriate flow:
 *  - Free products → email verification stepper (migrated from FreeUpgradePage)
 *  - Paid products → product summary + hosted Polar checkout
 *
 * Free products auto-authenticate after email verification. Paid products hand
 * off to Polar, then return to /purchase/:slug/complete for fulfillment status.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
    Camera,
    Link as LinkIcon,
    ArrowLeft,
    ArrowRight,
    Loader2,
    AlertCircle,
    CheckCircle,
    Mail,
    CreditCard,
    ShieldCheck,
    Check,
    ShoppingBag,
} from 'lucide-react';
import * as api from '../lib/api';
import { PRODUCT_DISPLAY, enrichProducts } from '../config/products';
import { usePortal } from '../context/PortalContext';
import { usePortalPageMeta } from '../hooks/usePortalPageMeta';
import { trackCheckoutStarted } from '../lib/analytics';

const ICON_MAP = { Camera, Link: LinkIcon };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ProductPurchasePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { actions } = usePortal();

    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Load product data
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const apiData = await api.fetchProducts();
                const enriched = enrichProducts(apiData);
                const found = enriched.find((p) => p.slug === slug);
                if (!cancelled) {
                    if (!found) {
                        navigate('/purchase', {
                            replace: true,
                            state: { error: `Product "${slug}" not found.` },
                        });
                        return;
                    }
                    setProduct(found);
                    setIsLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setLoadError(err.message || 'Failed to load product.');
                    setIsLoading(false);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [slug, navigate]);

    usePortalPageMeta({
        title: product
            ? `Buy ${product.name} — License Portal — HForgeX`
            : 'Buy Screenshot & OCR Pro — License Portal — HForgeX',
        description: product
            ? `${product.description} Complete checkout and receive your license by email.`
            : 'Buy Screenshot & OCR Pro and receive your license by email.',
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16 animate-fade-in">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="animate-fade-in">
                <div
                    className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                    role="alert"
                >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{loadError}</span>
                </div>
            </div>
        );
    }

    if (!product) return null;

    const activationContext = readActivationContext(searchParams);

    return product.isFree ? (
        <FreeFlow product={product} actions={actions} navigate={navigate} />
    ) : (
        <PaidFlow product={product} navigate={navigate} activationContext={activationContext} />
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// FREE FLOW — Email verification stepper
// ═══════════════════════════════════════════════════════════════════════════

function FreeFlow({ product, actions, navigate }) {
    const [step, setStep] = useState(1);
    const Icon = ICON_MAP[product.icon] || ShoppingBag;

    // Step 1 state
    const [email, setEmail] = useState('');
    const [stepOneError, setStepOneError] = useState(null);
    const [stepOneLoading, setStepOneLoading] = useState(false);

    // Step 2 state
    const [code, setCode] = useState('');
    const [stepTwoError, setStepTwoError] = useState(null);
    const [stepTwoLoading, setStepTwoLoading] = useState(false);

    // Step 3 state — auto-redirect
    const [redirecting, setRedirecting] = useState(false);
    const [redirectError, setRedirectError] = useState(null);
    const [orderResult, setOrderResult] = useState(null);

    async function handleStepOne(e) {
        e.preventDefault();
        const trimmedEmail = email.trim();
        setStepOneError(null);

        if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
            setStepOneError('Please enter a valid email address.');
            return;
        }

        setStepOneLoading(true);
        try {
            await api.requestFreeUpgrade(trimmedEmail, product.id);
            setStep(2);
        } catch (err) {
            if (err.status === 409) {
                setStepOneError('LICENSE_EXISTS');
            } else {
                setStepOneError(
                    err.message || 'Failed to send verification code. Please try again.'
                );
            }
        } finally {
            setStepOneLoading(false);
        }
    }

    async function handleStepTwo(e) {
        e.preventDefault();
        const trimmedCode = code.trim();
        setStepTwoError(null);

        if (!trimmedCode || trimmedCode.length !== 6 || !/^\d{6}$/.test(trimmedCode)) {
            setStepTwoError('Please enter the 6-digit code from your email.');
            return;
        }

        setStepTwoLoading(true);
        try {
            const result = await api.confirmFreeUpgrade(email.trim(), trimmedCode, product.id);
            setOrderResult(result);
            setStep(3);
            // Auto-redirect to /manage
            await autoRedirect(email.trim(), result.licenseId);
        } catch (err) {
            if (err.status === 410) {
                setStepTwoError('Code expired. Go back and request a new one.');
            } else if (err.status === 429) {
                setStepTwoError('Too many attempts. Please request a new code.');
            } else {
                setStepTwoError(err.message || 'Verification failed. Please try again.');
            }
        } finally {
            setStepTwoLoading(false);
        }
    }

    async function autoRedirect(userEmail, licenseKey) {
        setRedirecting(true);
        try {
            await actions.lookup(userEmail, licenseKey);
            navigate('/manage', { replace: true });
        } catch {
            setRedirecting(false);
            setRedirectError(
                'License created successfully. Use your license key below to sign in.'
            );
        }
    }

    return (
        <div className="animate-fade-in">
            {/* Back navigation */}
            {step < 3 && (
                <button
                    type="button"
                    onClick={() => (step === 2 ? setStep(1) : navigate('/purchase'))}
                    className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {step === 2 ? 'Back' : 'All products'}
                </button>
            )}

            {/* Free indicator banner */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 mb-6">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    Free upgrade — email verification only, no payment required
                </span>
            </div>

            {/* Step indicators */}
            <div className="flex items-center gap-3 mb-6">
                <StepDot active={step === 1} completed={step > 1} label="1" />
                <div className="flex-1 h-px bg-zinc-300 dark:bg-zinc-700" />
                <StepDot active={step === 2} completed={step > 2} label="2" />
                <div className="flex-1 h-px bg-zinc-300 dark:bg-zinc-700" />
                <StepDot active={step === 3} completed={false} label="3" />
            </div>

            {step === 1 && (
                <div>
                    <div className="text-center mb-6">
                        <Icon className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
                        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                            {product.name} — Free Pro Upgrade
                        </h2>
                        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                            Verify your email to unlock Pro features at no cost.
                        </p>
                    </div>

                    <form onSubmit={handleStepOne} className="space-y-4" noValidate>
                        <div>
                            <label
                                htmlFor="upgrade-email"
                                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                            >
                                Email Address
                            </label>
                            <input
                                id="upgrade-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                autoComplete="email"
                                autoFocus
                                className="w-full px-3 py-2.5 text-sm rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>

                        {stepOneError && (
                            <div
                                className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                                role="alert"
                            >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                {stepOneError === 'LICENSE_EXISTS' ? (
                                    <span>
                                        A Pro license already exists for this email.{' '}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate('/lookup', {
                                                    state: { email: email.trim() },
                                                })
                                            }
                                            className="font-medium underline hover:text-red-700 dark:hover:text-red-300"
                                        >
                                            Go to License Lookup
                                        </button>{' '}
                                        to manage it.
                                    </span>
                                ) : (
                                    <span>{stepOneError}</span>
                                )}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={stepOneLoading}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {stepOneLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <ArrowRight className="w-4 h-4" />
                            )}
                            {stepOneLoading ? 'Sending code…' : 'Send Verification Code'}
                        </button>
                    </form>
                </div>
            )}

            {step === 2 && (
                <div>
                    <div className="text-center mb-6">
                        <Mail className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
                        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                            Check Your Email
                        </h2>
                        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                            We sent a 6-digit code to{' '}
                            <strong className="text-zinc-700 dark:text-zinc-200">
                                {email.trim()}
                            </strong>
                            . Enter it below.
                        </p>
                    </div>

                    <form onSubmit={handleStepTwo} className="space-y-4" noValidate>
                        <div>
                            <label
                                htmlFor="verify-code"
                                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                            >
                                Verification Code
                            </label>
                            <input
                                id="verify-code"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                value={code}
                                onChange={(e) =>
                                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                                }
                                placeholder="000000"
                                autoFocus
                                autoComplete="one-time-code"
                                className="w-full px-3 py-2.5 text-sm rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-center font-mono text-lg tracking-[0.3em]"
                            />
                        </div>

                        {stepTwoError && (
                            <div
                                className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                                role="alert"
                            >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{stepTwoError}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={stepTwoLoading}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {stepTwoLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <CheckCircle className="w-4 h-4" />
                            )}
                            {stepTwoLoading ? 'Verifying…' : 'Verify & Activate'}
                        </button>
                    </form>
                </div>
            )}

            {step === 3 && (
                <div className="text-center">
                    {redirecting ? (
                        <div className="py-8">
                            <Loader2 className="w-8 h-8 mx-auto text-emerald-500 animate-spin mb-4" />
                            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                                Activating your license…
                            </h2>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                Redirecting to your license dashboard.
                            </p>
                        </div>
                    ) : (
                        <div className="py-4">
                            <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                            <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                                Pro Upgraded!
                            </h2>
                            {redirectError && (
                                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 mb-4 text-sm text-amber-700 dark:text-amber-300">
                                    <p>{redirectError}</p>
                                    {orderResult?.licenseId && (
                                        <p className="mt-2 font-mono text-xs bg-white dark:bg-zinc-800 rounded px-2 py-1 inline-block">
                                            License key: {orderResult.licenseId}
                                        </p>
                                    )}
                                </div>
                            )}
                            <Link
                                to="/lookup"
                                state={{ email: email.trim() }}
                                className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                            >
                                Go to License Lookup <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAID FLOW — hosted Polar checkout
// ═══════════════════════════════════════════════════════════════════════════

function PaidFlow({ product, navigate, activationContext }) {
    const Icon = ICON_MAP[product.icon] || ShoppingBag;

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(null);
    const [checkoutState, setCheckoutState] = useState('idle'); // idle | loading-sdk | checkout
    const [checkoutContext, setCheckoutContext] = useState(activationContext);
    const [pollError, setPollError] = useState(null);

    async function handleBuyClick() {
        const trimmedEmail = email.trim();
        setEmailError(null);
        setPollError(null);

        if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
            setEmailError('Please enter your email so we can deliver your license.');
            return;
        }

        if (!product.checkoutUrl) {
            setCheckoutState('idle');
            setPollError('Checkout is not configured for this product yet.');
            return;
        }

        setCheckoutState('loading-sdk');

        // Build Polar checkout URL with email pre-fill and product metadata.
        const checkoutContext = {
            ...activationContext,
            activationClaim: activationContext.activationClaim || createActivationClaim(),
        };
        setCheckoutContext(checkoutContext);
        const checkoutUrl = new URL(product.checkoutUrl);
        const successUrl = buildCheckoutSuccessUrl(product.slug, checkoutContext);
        const returnUrl = buildCheckoutReturnUrl(product.slug, checkoutContext);
        rememberCheckout(product.slug, {
            email: trimmedEmail,
            productId: product.id,
            ...checkoutContext,
            startedAt: new Date().toISOString(),
        });

        checkoutUrl.searchParams.set('customer_email', trimmedEmail);
        checkoutUrl.searchParams.set('metadata[product_id]', product.id);
        checkoutUrl.searchParams.set(
            'metadata[source]',
            checkoutContext.source || 'license-portal'
        );
        checkoutUrl.searchParams.set('metadata[product_slug]', product.slug);
        checkoutUrl.searchParams.set('metadata[activation_claim]', checkoutContext.activationClaim);
        if (checkoutContext.deviceCode) {
            checkoutUrl.searchParams.set('metadata[device_code_present]', 'true');
            checkoutUrl.searchParams.set('metadata[device_code]', checkoutContext.deviceCode);
        }
        if (checkoutContext.feature) {
            checkoutUrl.searchParams.set('metadata[feature]', checkoutContext.feature);
        }
        checkoutUrl.searchParams.set('success_url', successUrl);
        checkoutUrl.searchParams.set('return_url', returnUrl);

        logCheckoutStart({
            checkoutUrl,
            email: trimmedEmail,
            productId: product.id,
            productSlug: product.slug,
            activationContext: checkoutContext,
        });
        trackCheckoutStarted({
            productSlug: product.slug,
            productId: product.id,
            source: checkoutContext.source,
            feature: checkoutContext.feature,
            hasDeviceCode: Boolean(checkoutContext.deviceCode),
        });

        setCheckoutState('checkout');

        window.open(checkoutUrl.toString(), '_blank', 'noopener,noreferrer');
    }

    return (
        <div className="animate-fade-in">
            {/* Back */}
            {checkoutState === 'idle' && (
                <button
                    type="button"
                    onClick={() => navigate('/purchase')}
                    className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    All products
                </button>
            )}

            {/* Paid indicator banner */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 mb-6">
                <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                    ${product.price} {product.currency} — One-time purchase, own forever
                </span>
            </div>

            {(checkoutState === 'idle' || checkoutState === 'loading-sdk') && (
                <div>
                    {/* Product summary */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-5 mb-6">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="shrink-0 w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                                <Icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                                    {product.name}
                                </h2>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                                    {product.description}
                                </p>
                            </div>
                        </div>

                        <ul className="space-y-2 mb-4">
                            {product.highlights.map((h) => (
                                <li
                                    key={h}
                                    className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400"
                                >
                                    <Check className="w-4 h-4 shrink-0 text-emerald-500" />
                                    {h}
                                </li>
                            ))}
                        </ul>

                        {product.moneyBack && (
                            <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 pt-3 border-t border-zinc-200 dark:border-zinc-700">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                {product.moneyBack}
                            </div>
                        )}
                    </div>

                    {/* Email + buy form */}
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="purchase-email"
                                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                            >
                                Email Address
                            </label>
                            <input
                                id="purchase-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                autoComplete="email"
                                autoFocus
                                className={`w-full px-3 py-2.5 text-sm rounded-lg border bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                    emailError
                                        ? 'border-red-400 dark:border-red-500'
                                        : 'border-zinc-300 dark:border-zinc-600'
                                }`}
                            />
                            {emailError && (
                                <p
                                    className="mt-1 text-xs text-red-600 dark:text-red-400"
                                    role="alert"
                                >
                                    {emailError}
                                </p>
                            )}
                            <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
                                Your license key will be sent to this email.
                            </p>
                        </div>

                        {pollError && (
                            <div
                                className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                                role="alert"
                            >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{pollError}</span>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={handleBuyClick}
                            disabled={checkoutState === 'loading-sdk'}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {checkoutState === 'loading-sdk' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <CreditCard className="w-4 h-4" />
                            )}
                            {checkoutState === 'loading-sdk'
                                ? 'Loading checkout…'
                                : product.ctaLabel}
                        </button>
                    </div>
                </div>
            )}

            {checkoutState === 'checkout' && (
                <div className="text-center py-8">
                    <CreditCard className="w-10 h-10 mx-auto text-indigo-500 mb-4" />
                    <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                        Complete your purchase
                    </h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                        The Polar checkout window should be open. After payment, it will return to
                        HForgeX and show your license status.
                    </p>
                    <Link
                        to={buildCompletePath(product.slug, checkoutContext)}
                        state={{
                            email: email.trim(),
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        I completed checkout <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            )}
        </div>
    );
}

function logCheckoutStart({ checkoutUrl, email, productId, productSlug, activationContext = {} }) {
    const metadata = {
        product_id: productId,
        product_slug: productSlug,
        source: activationContext.source || 'license-portal',
    };
    if (activationContext.feature) metadata.feature = activationContext.feature;
    if (activationContext.deviceCode) metadata.device_code_present = true;
    if (activationContext.activationClaim) metadata.activation_claim_present = true;

    console.info('[license-portal] Starting Polar checkout', {
        checkoutOrigin: checkoutUrl.origin,
        checkoutPath: checkoutUrl.pathname,
        customerEmail: maskEmail(email),
        metadata,
    });
}

function buildCheckoutSuccessUrl(productSlug, activationContext = {}) {
    const url = new URL(`/purchase/${productSlug}/complete`, window.location.origin);
    url.searchParams.set('checkout_id', '{CHECKOUT_ID}');
    appendActivationContext(url, activationContext);
    return url.toString();
}

function buildCheckoutReturnUrl(productSlug, activationContext = {}) {
    const url = new URL(`/purchase/${productSlug}`, window.location.origin);
    appendActivationContext(url, activationContext);
    return url.toString();
}

function buildCompletePath(productSlug, activationContext = {}) {
    const url = new URL(`/purchase/${productSlug}/complete`, window.location.origin);
    appendActivationContext(url, activationContext);
    return `${url.pathname}${url.search}`;
}

function rememberCheckout(productSlug, payload) {
    try {
        window.localStorage.setItem(`hforgex:checkout:${productSlug}`, JSON.stringify(payload));
    } catch {
        // Best effort only. The completion page can still fall back to email copy.
    }
}

function readActivationContext(searchParams) {
    return {
        deviceCode: searchParams.get('device_code') || '',
        feature: searchParams.get('feature') || '',
        source: searchParams.get('source') || '',
        activationClaim: searchParams.get('activation_claim') || '',
    };
}

function appendActivationContext(url, activationContext = {}) {
    if (activationContext.activationClaim) {
        url.searchParams.set('activation_claim', activationContext.activationClaim);
    }
    if (activationContext.deviceCode) {
        url.searchParams.set('device_code', activationContext.deviceCode);
    }
    if (activationContext.feature) {
        url.searchParams.set('feature', activationContext.feature);
    }
    if (activationContext.source) {
        url.searchParams.set('source', activationContext.source);
    }
}

function createActivationClaim() {
    if (crypto.randomUUID) return crypto.randomUUID();

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return [...bytes]
        .map((byte, index) => {
            const value = byte.toString(16).padStart(2, '0');
            return [4, 6, 8, 10].includes(index) ? `-${value}` : value;
        })
        .join('');
}

function maskEmail(email) {
    const [local, domain] = email.split('@');
    if (!local || !domain) return '(invalid email)';
    const visible = local.slice(0, 2);
    return `${visible}${'*'.repeat(Math.max(local.length - visible.length, 1))}@${domain}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function StepDot({ active, completed, label }) {
    const base =
        'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors';
    if (completed) {
        return (
            <div className={`${base} bg-emerald-500 text-white`}>
                <CheckCircle className="w-4 h-4" />
            </div>
        );
    }
    if (active) {
        return <div className={`${base} bg-indigo-600 dark:bg-indigo-500 text-white`}>{label}</div>;
    }
    return (
        <div className={`${base} bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400`}>
            {label}
        </div>
    );
}
